import type { FunctionReturnType } from 'convex/server';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { text as sharedText } from '@shared/text';
import {
  getSowingConfigForPlant,
  type UserPlantDefault,
} from '@modules/garden-config/business_logic';
import type { Garden } from '@modules/my-gardens/business_logic';
import type {
  AgendaSection,
  CalendarDayCell,
  ClimateRegion,
  Hemisphere,
  SowMethod,
  SowingByRegion,
  SowingCalendarEntry,
  SowingCalendarStatus,
  WeekDayColumn,
} from './models';

export type { Garden };
export type {
  AgendaSection,
  CalendarDayCell,
  ClimateRegion,
  Hemisphere,
  SowMethod,
  SowingCalendarEntry,
  SowingCalendarStatus,
  SowingCalendarViewMode,
  WeekDayColumn,
} from './models';

export type Plant = FunctionReturnType<typeof api.plants.queries.getAllPlants>[number];

export type PlantSowingConfig = FunctionReturnType<
  typeof api.plantSowingConfig.queries.getSowingConfigsByGarden
>[number];

export type SeedTrackingRow = FunctionReturnType<
  typeof api.seedTracking.queries.getSeedTracking
>[number];

export type UserPreferences = FunctionReturnType<
  typeof api.userPreferences.queries.getUserPreferences
>;

export type { UserPlantDefault };

const DEFAULT_REGION: ClimateRegion = 'temperate';

const MONTH_SHORT = sharedText.months.map((m) => m.slice(0, 3));

export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseLocalISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function getMondayOfWeekLocal(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d;
}

export function resolveSowMonths(
  sowMonthsSouthern: SowingByRegion | undefined,
  sowMonthsNorthern: SowingByRegion | undefined,
  hemisphere: Hemisphere,
  region: ClimateRegion
): number[] {
  const byHemisphere = hemisphere === 'northern' ? sowMonthsNorthern : sowMonthsSouthern;
  if (!byHemisphere) return [];
  return byHemisphere[region] ?? byHemisphere.temperate ?? [];
}

export function formatSowWindowLabel(months: number[]): string {
  if (months.length === 0) return '';
  const sorted = [...new Set(months)].sort((a, b) => a - b);
  const names = sorted.map((m) => MONTH_SHORT[m - 1] ?? String(m));
  if (names.length === 1) return names[0];
  return `${names[0]} – ${names[names.length - 1]}`;
}

/** Label for configured sow timing, e.g. "Aug · 1". */
export function formatSowTimingLabel(sowMonth?: number, sowWeekOfMonth?: number): string {
  if (sowMonth === undefined) return '';
  const month = MONTH_SHORT[sowMonth - 1] ?? String(sowMonth);
  if (sowWeekOfMonth === undefined) return month;
  return `${month} · ${sowWeekOfMonth}`;
}

export function resolveSowMethod(canSowIndoors?: boolean, canSowOutdoors?: boolean): SowMethod {
  if (canSowOutdoors) return 'outdoor';
  if (canSowIndoors) return 'indoor';
  return 'outdoor';
}

export function countSlotsBySpecies(garden: Garden, plants: Plant[]): Map<string, number> {
  const plantsById = new Map(plants.map((p) => [p.id, p]));
  const counts = new Map<string, number>();

  for (const placement of garden.plants) {
    const plant = plantsById.get(placement.plantId);
    const speciesId = plant?.speciesId ?? placement.plantId;
    counts.set(speciesId, (counts.get(speciesId) ?? 0) + 1);
  }

  return counts;
}

export function getSpeciesPlantsInGarden(garden: Garden | null, allPlants: Plant[]): Plant[] {
  if (!garden) return [];
  const uniqueIds = [...new Set(garden.plants.map((p) => p.plantId))];
  const speciesIdSet = new Set<string>();

  for (const id of uniqueIds) {
    const plant = allPlants.find((p) => p.id === id);
    speciesIdSet.add(plant?.speciesId ?? id);
  }

  return allPlants.filter(
    (p) => speciesIdSet.has(p.id) && (p.speciesId === undefined || p.speciesId === p.id)
  );
}

/** First day of the Nth week-of-month (1-indexed month and week). */
export function dateForSowWeekOfMonth(year: number, month: number, weekOfMonth: number): Date {
  const lastDay = new Date(year, month, 0).getDate();
  const day = Math.min((weekOfMonth - 1) * 7 + 1, lastDay);
  return new Date(year, month - 1, day);
}

function endOfSowWeek(start: Date): Date {
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return end.getTime() > monthEnd.getTime() ? monthEnd : end;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Status from garden-config sow month/week (catalog defaults applied upstream).
 * Pins due-now to today; upcoming to the target week start (wrapping to next year if past).
 * Sown keeps a pin on this year's sow week (or today while in that week) so it stays on the calendar.
 */
export function resolveEntryStatus(args: {
  hasSown: boolean;
  today: Date;
  sowMonth?: number;
  sowWeekOfMonth?: number;
}): { status: SowingCalendarStatus; pinDate: string | null } {
  const { hasSown, today, sowMonth, sowWeekOfMonth } = args;

  if (hasSown) {
    if (sowMonth === undefined || sowWeekOfMonth === undefined) {
      return { status: 'sown', pinDate: null };
    }
    const weekStart = dateForSowWeekOfMonth(today.getFullYear(), sowMonth, sowWeekOfMonth);
    const weekEnd = endOfSowWeek(weekStart);
    const todayDay = startOfLocalDay(today);
    const inSowWeek =
      todayDay.getTime() >= weekStart.getTime() && todayDay.getTime() <= weekEnd.getTime();
    return {
      status: 'sown',
      pinDate: toLocalISODate(inSowWeek ? todayDay : weekStart),
    };
  }

  if (sowMonth === undefined || sowWeekOfMonth === undefined) {
    return { status: 'out-of-season', pinDate: null };
  }

  const todayDay = startOfLocalDay(today);
  let weekStart = dateForSowWeekOfMonth(today.getFullYear(), sowMonth, sowWeekOfMonth);
  let weekEnd = endOfSowWeek(weekStart);

  if (todayDay.getTime() > weekEnd.getTime()) {
    weekStart = dateForSowWeekOfMonth(today.getFullYear() + 1, sowMonth, sowWeekOfMonth);
    return { status: 'upcoming', pinDate: toLocalISODate(weekStart) };
  }

  if (todayDay.getTime() < weekStart.getTime()) {
    return { status: 'upcoming', pinDate: toLocalISODate(weekStart) };
  }

  return { status: 'due-now', pinDate: toLocalISODate(today) };
}

export function buildSowingCalendarEntries(args: {
  garden: Garden | null;
  plants: Plant[];
  configs: PlantSowingConfig[];
  seedTracking: SeedTrackingRow[];
  hemisphere: Hemisphere;
  region: ClimateRegion;
  plantDefaults?: UserPlantDefault[];
  today?: Date;
}): SowingCalendarEntry[] {
  const {
    garden,
    plants,
    configs,
    seedTracking,
    hemisphere,
    region,
    plantDefaults = [],
    today = new Date(),
  } = args;

  if (!garden) return [];

  const speciesPlants = getSpeciesPlantsInGarden(garden, plants);
  const slotCounts = countSlotsBySpecies(garden, plants);
  const sownByPlant = new Map(seedTracking.map((s) => [s.plantId, s.hasSown]));
  const preferences = { hemisphere, region };

  const entries: SowingCalendarEntry[] = [];

  for (const plant of speciesPlants) {
    const config = getSowingConfigForPlant(
      configs,
      garden._id,
      plant.id,
      plant.category,
      plantDefaults,
      plant,
      preferences
    );
    if (!config.isActive) continue;

    const sowMonths = resolveSowMonths(
      plant.sowMonthsSouthern,
      plant.sowMonthsNorthern,
      hemisphere,
      region
    );

    const slots = slotCounts.get(plant.id) ?? 0;
    const buffer = config.seedBuffer;
    const hasSown = sownByPlant.get(plant.id) ?? false;
    const { status, pinDate } = resolveEntryStatus({
      hasSown,
      today,
      sowMonth: config.sowMonth,
      sowWeekOfMonth: config.sowWeekOfMonth,
    });

    entries.push({
      plantId: plant.id,
      plantName: plant.name,
      emoji: plant.emoji,
      imagePath: plant.imagePath,
      sowMethod: resolveSowMethod(plant.canSowIndoors, plant.canSowOutdoors),
      status,
      slots,
      buffer,
      seeds: slots + buffer,
      sowMonths,
      windowLabel: formatSowTimingLabel(config.sowMonth, config.sowWeekOfMonth),
      hasSown,
      pinDate,
    });
  }

  return entries.sort((a, b) => a.plantName.localeCompare(b.plantName));
}

export function groupAgendaSections(entries: SowingCalendarEntry[]): AgendaSection[] {
  const order: SowingCalendarStatus[] = ['due-now', 'upcoming', 'out-of-season'];
  return order.map((id) => ({
    id,
    entries: entries.filter((e) => e.status === id),
  }));
}

export function buildMonthGrid(args: {
  focusedDate: Date;
  today: Date;
  entries: SowingCalendarEntry[];
}): CalendarDayCell[] {
  const { focusedDate, today, entries } = args;
  const year = focusedDate.getFullYear();
  const month = focusedDate.getMonth();
  const todayIso = toLocalISODate(today);

  const firstOfMonth = new Date(year, month, 1);
  const gridStart = getMondayOfWeekLocal(firstOfMonth);
  const lastOfMonth = new Date(year, month + 1, 0);
  const lastMonday = getMondayOfWeekLocal(lastOfMonth);
  const gridEnd = new Date(lastMonday);
  gridEnd.setDate(gridEnd.getDate() + 6);

  const entriesByDate = new Map<string, SowingCalendarEntry[]>();
  for (const entry of entries) {
    if (!entry.pinDate) continue;
    const list = entriesByDate.get(entry.pinDate) ?? [];
    list.push(entry);
    entriesByDate.set(entry.pinDate, list);
  }

  const cells: CalendarDayCell[] = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const isoDate = toLocalISODate(cursor);
    cells.push({
      isoDate,
      dayOfMonth: cursor.getDate(),
      inCurrentMonth: cursor.getMonth() === month,
      isToday: isoDate === todayIso,
      entries: entriesByDate.get(isoDate) ?? [],
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return cells;
}

export function buildWeekColumns(args: {
  focusedDate: Date;
  today: Date;
  entries: SowingCalendarEntry[];
}): WeekDayColumn[] {
  const { focusedDate, today, entries } = args;
  const monday = getMondayOfWeekLocal(focusedDate);
  const todayIso = toLocalISODate(today);

  const weekDates = new Set<string>();
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    weekDates.add(toLocalISODate(date));
  }

  const entriesByDate = new Map<string, SowingCalendarEntry[]>();
  for (const entry of entries) {
    if (!entry.pinDate || !weekDates.has(entry.pinDate)) continue;
    if (entry.status !== 'due-now' && entry.status !== 'upcoming' && entry.status !== 'sown') {
      continue;
    }
    const list = entriesByDate.get(entry.pinDate) ?? [];
    list.push(entry);
    entriesByDate.set(entry.pinDate, list);
  }

  const columns: WeekDayColumn[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const isoDate = toLocalISODate(date);
    columns.push({
      isoDate,
      dayName: sharedText.daysOfWeekMondayFirst[i],
      dayOfMonth: date.getDate(),
      isToday: isoDate === todayIso,
      entries: entriesByDate.get(isoDate) ?? [],
    });
  }

  return columns;
}

export function weekSummary(entries: SowingCalendarEntry[]): {
  actionCount: number;
  seedCount: number;
} {
  const due = entries.filter((e) => e.status === 'due-now');
  return {
    actionCount: due.length,
    seedCount: due.reduce((sum, e) => sum + e.seeds, 0),
  };
}

export function footerEntries(
  entries: SowingCalendarEntry[],
  statuses: SowingCalendarStatus[]
): SowingCalendarEntry[] {
  return entries.filter((e) => statuses.includes(e.status));
}

export function resolvePreferences(prefs: UserPreferences | null | undefined): {
  hemisphere: Hemisphere;
  region: ClimateRegion;
} {
  return {
    hemisphere: prefs?.hemisphere ?? 'southern',
    region: prefs?.region ?? DEFAULT_REGION,
  };
}

export function shiftFocusedMonth(isoFocusedDate: string, deltaMonths: number): string {
  const date = parseLocalISODate(isoFocusedDate);
  date.setDate(1);
  date.setMonth(date.getMonth() + deltaMonths);
  return toLocalISODate(date);
}

export function shiftFocusedWeek(isoFocusedDate: string, deltaWeeks: number): string {
  const date = parseLocalISODate(isoFocusedDate);
  date.setDate(date.getDate() + deltaWeeks * 7);
  return toLocalISODate(date);
}

export function monthLabel(isoFocusedDate: string): string {
  const date = parseLocalISODate(isoFocusedDate);
  return `${sharedText.months[date.getMonth()]} ${date.getFullYear()}`;
}

export function weekRangeLabel(isoFocusedDate: string): string {
  const monday = getMondayOfWeekLocal(parseLocalISODate(isoFocusedDate));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
  return `${fmt(monday)} – ${fmt(sunday)}, ${sunday.getFullYear()}`;
}

type ToggleSeedSownFn = (args: {
  gardenId: Id<'gardens'>;
  plantId: string;
  hasSown: boolean;
}) => Promise<unknown>;

export function createHandleMarkSown(
  selectedGarden: Garden | null,
  toggleSeedSown: ToggleSeedSownFn
) {
  return async (plantId: string, currentHasSown: boolean) => {
    if (!selectedGarden) return;
    await toggleSeedSown({
      gardenId: selectedGarden._id,
      plantId,
      hasSown: !currentHasSown,
    });
  };
}

/** Active Mark sown — design only shows this for due-now rows. */
export function canMarkSown(status: SowingCalendarStatus): boolean {
  return status === 'due-now';
}

export const MONTH_CELL_VISIBLE_ENTRIES = 2;

export function monthCellVisibleEntries(entries: SowingCalendarEntry[]): {
  visible: SowingCalendarEntry[];
  overflowCount: number;
} {
  const visible = entries.slice(0, MONTH_CELL_VISIBLE_ENTRIES);
  return {
    visible,
    overflowCount: Math.max(0, entries.length - MONTH_CELL_VISIBLE_ENTRIES),
  };
}

export function formatShortDateLabel(isoDate: string): string {
  const date = parseLocalISODate(isoDate);
  const months = sharedText.months.map((m) => m.slice(0, 3));
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** Month + day only, for agenda time buckets (e.g. "Sep 1"). */
export function formatMonthDayLabel(isoDate: string): string {
  const date = parseLocalISODate(isoDate);
  const months = sharedText.months.map((m) => m.slice(0, 3));
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

export type TagVariant = 'accent' | 'outline' | 'neutral';

export function tagVariantForEntry(entry: SowingCalendarEntry): TagVariant {
  if (entry.status === 'sown') return 'neutral';
  if (entry.status === 'due-now' && entry.sowMethod === 'outdoor') return 'accent';
  return 'outline';
}
